import { useAuthContext } from '@/auth/useAuthContext';
import { Modal } from '@/components/Modal';
import { ApiError, createService, deleteService, listServices, updateService } from '@/lib/api';
import { formatPriceMinor } from '@/lib/format';

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: '£',
  USD: '$',
  EUR: '€',
  CAD: '$',
  AUD: '$',
  NZD: '$',
};
function currencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency] ?? currency;
}
import type {
  ApplicableTo,
  CreateServiceInput,
  ServiceCatalogueItem,
  UpdateServiceInput,
} from '@/lib/types';
import {
  AlertTriangle,
  Edit2,
  ExternalLink,
  Moon,
  Plus,
  Scissors,
  Sparkles,
  Sun,
  ToggleLeft,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PRICING_MODELS = [
  { value: 'flat', label: 'Flat' },
  { value: 'per_day', label: 'Per day' },
  { value: 'per_hour', label: 'Per hour' },
  { value: 'per_unit', label: 'Per unit' },
] as const;

type PricingModel = 'flat' | 'per_day' | 'per_hour' | 'per_unit';

type CoreCategory = 'boarding' | 'daycare' | 'grooming';

const CATEGORY_OPTIONS = [
  { value: 'boarding', label: 'Boarding (per night)' },
  { value: 'daycare', label: 'Daycare (per visit)' },
  { value: 'grooming', label: 'Grooming (per appointment)' },
  { value: 'retail', label: 'Retail product' },
  { value: 'other', label: 'Other / custom' },
] as const;

/** Default `applicableTo` for a primary service — it IS the booking, not an add-on. */
const PRIMARY_APPLICABLE_TO: ApplicableTo = {
  boarding_addon: false,
  daycare_addon: false,
  grooming_addon: false,
  standalone: true,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
}

function parseApplicableTo(raw: string): ApplicableTo {
  try {
    return JSON.parse(raw) as ApplicableTo;
  } catch {
    return {
      boarding_addon: false,
      daycare_addon: false,
      grooming_addon: false,
      standalone: false,
    };
  }
}

function parseSpeciesAllowed(raw: string): ('dog' | 'cat')[] {
  try {
    return JSON.parse(raw) as ('dog' | 'cat')[];
  } catch {
    return ['dog'];
  }
}

function formatApplicableTo(applicableTo: ApplicableTo): string {
  const parts: string[] = [];
  if (applicableTo.boarding_addon) parts.push('Boarding');
  if (applicableTo.daycare_addon) parts.push('Daycare');
  if (applicableTo.grooming_addon) parts.push('Grooming');
  if (applicableTo.standalone) parts.push('Standalone');
  return parts.length > 0 ? parts.join(', ') : 'None';
}

/**
 * Migration 0088 — convert the form's percent string to basis points.
 * '' = inherit the account rate (null); '0' = zero-rated/exempt (0 bps).
 */
function taxRateBpsFromPct(pct: string): number | null {
  const t = pct.trim();
  if (t === '') return null;
  const n = Number.parseFloat(t);
  if (Number.isNaN(n) || n < 0 || n > 100) return null;
  return Math.round(n * 100);
}

/** True when the tax field holds either '' (inherit) or a valid 0–100 percent. */
function isTaxRatePctValid(pct: string): boolean {
  const t = pct.trim();
  if (t === '') return true;
  const n = Number.parseFloat(t);
  return !Number.isNaN(n) && n >= 0 && n <= 100;
}

/** Human-readable pricing model label combining category context. */
function pricingModelLabel(category: string, model: PricingModel): string {
  if (category === 'boarding' && model === 'per_day') return 'per night';
  if (category === 'daycare' && model === 'flat') return 'per visit';
  if (category === 'grooming' && model === 'flat') return 'per appointment';
  if (model === 'flat') return 'per booking';
  if (model === 'per_day') return 'per day';
  if (model === 'per_hour') return 'per hour';
  if (model === 'per_unit') return 'per unit';
  return model;
}

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

interface ServiceFormState {
  name: string;
  slug: string;
  description: string;
  category: string;
  iconSlug: string;
  applicableTo: ApplicableTo;
  requiresAppointment: boolean;
  defaultDurationMin: string;
  defaultPricingModel: PricingModel;
  defaultPriceMinor: string;
  speciesAllowed: ('dog' | 'cat')[];
  paymentTiming: 'full' | 'deposit';
  depositKind: 'percent' | 'fixed';
  depositPct: string;
  depositAmountMinor: string;
  balanceDueAt: 'checkin' | 'checkout' | '';
  /** Tax rate override as a percent string; '' = inherit the account rate. */
  taxRatePct: string;
  /** Migration 0095 — 'instant' confirms online bookings immediately; 'request' queues them for approval. */
  bookingConfirmation: 'instant' | 'request';
}

function defaultFormState(): ServiceFormState {
  return {
    name: '',
    slug: '',
    description: '',
    category: '',
    iconSlug: '',
    applicableTo: {
      boarding_addon: true,
      daycare_addon: false,
      grooming_addon: false,
      standalone: false,
    },
    requiresAppointment: false,
    defaultDurationMin: '',
    defaultPricingModel: 'flat',
    defaultPriceMinor: '0.00',
    speciesAllowed: ['dog'],
    paymentTiming: 'full',
    depositKind: 'percent',
    depositPct: '20',
    depositAmountMinor: '0.00',
    balanceDueAt: '',
    taxRatePct: '',
    bookingConfirmation: 'instant',
  };
}

function coreFormState(category: CoreCategory): ServiceFormState {
  const nameMap: Record<CoreCategory, string> = {
    boarding: 'Boarding',
    daycare: 'Daycare',
    grooming: 'Grooming',
  };
  const modelMap: Record<CoreCategory, PricingModel> = {
    boarding: 'per_day',
    daycare: 'flat',
    grooming: 'flat',
  };
  const name = nameMap[category];
  return {
    name,
    slug: slugify(name),
    description: '',
    category,
    iconSlug: '',
    applicableTo: PRIMARY_APPLICABLE_TO,
    requiresAppointment: false,
    defaultDurationMin: '',
    defaultPricingModel: modelMap[category],
    defaultPriceMinor: '0.00',
    speciesAllowed: ['dog'],
    paymentTiming: 'full',
    depositKind: 'percent',
    depositPct: '20',
    depositAmountMinor: '0.00',
    balanceDueAt: '',
    taxRatePct: '',
    bookingConfirmation: 'instant',
  };
}

function formFromService(svc: ServiceCatalogueItem): ServiceFormState {
  const applicableTo = parseApplicableTo(svc.applicableTo);
  const speciesAllowed = parseSpeciesAllowed(svc.speciesAllowed);
  return {
    name: svc.name,
    slug: svc.slug,
    description: svc.description ?? '',
    category: svc.category,
    iconSlug: svc.iconSlug ?? '',
    applicableTo,
    requiresAppointment: svc.requiresAppointment === 1,
    defaultDurationMin: svc.defaultDurationMin != null ? String(svc.defaultDurationMin) : '',
    defaultPricingModel: svc.defaultPricingModel as PricingModel,
    defaultPriceMinor: (svc.defaultPriceMinor / 100).toFixed(2),
    speciesAllowed,
    paymentTiming: (svc.paymentTiming ?? 'full') as 'full' | 'deposit',
    depositKind: (svc.depositKind ?? 'percent') as 'percent' | 'fixed',
    depositPct: svc.depositPct != null ? String(svc.depositPct) : '20',
    depositAmountMinor:
      svc.depositAmountMinor != null ? (svc.depositAmountMinor / 100).toFixed(2) : '0.00',
    balanceDueAt: (svc.balanceDueAt ?? '') as 'checkin' | 'checkout' | '',
    taxRatePct: svc.taxRateBps != null ? String(svc.taxRateBps / 100) : '',
    bookingConfirmation: svc.bookingConfirmation === 'request' ? 'request' : 'instant',
  };
}

// ---------------------------------------------------------------------------
// Service form (shared between Add and Edit)
// ---------------------------------------------------------------------------

interface ServiceFormProps {
  initial: ServiceFormState;
  busy: boolean;
  error: string | null;
  submitLabel: string;
  currSym: string;
  /**
   * Migration 0088 — per-service tax override. Non-null only when the account
   * has the opt-in tax engine enabled; the field is hidden otherwise.
   */
  taxConfig: { label: string; accountRatePct: string } | null;
  onSubmit: (form: ServiceFormState) => void;
  onCancel: () => void;
}

function ServiceForm({
  initial,
  busy,
  error,
  submitLabel,
  currSym,
  taxConfig,
  onSubmit,
  onCancel,
}: ServiceFormProps) {
  const [form, setForm] = useState<ServiceFormState>(initial);

  function toggleSpecies(s: 'dog' | 'cat') {
    setForm((prev) => {
      const has = prev.speciesAllowed.includes(s);
      if (has && prev.speciesAllowed.length === 1) return prev;
      return {
        ...prev,
        speciesAllowed: has
          ? prev.speciesAllowed.filter((x) => x !== s)
          : [...prev.speciesAllowed, s],
      };
    });
  }

  function toggleApplicable(key: keyof ApplicableTo) {
    setForm((prev) => ({
      ...prev,
      applicableTo: { ...prev.applicableTo, [key]: !prev.applicableTo[key] },
    }));
  }

  function handleNameChange(name: string) {
    setForm((prev) => ({
      ...prev,
      name,
      // Auto-derive slug from name only when slug hasn't been manually edited
      slug: prev.slug === slugify(prev.name) ? slugify(name) : prev.slug,
    }));
  }

  function handleCategoryChange(category: string) {
    setForm((prev) => {
      const isPrimary =
        category === 'boarding' || category === 'daycare' || category === 'grooming';
      const modelMap: Record<string, PricingModel> = {
        boarding: 'per_day',
        daycare: 'flat',
        grooming: 'flat',
      };
      return {
        ...prev,
        category,
        ...(isPrimary
          ? {
              defaultPricingModel: modelMap[category],
              applicableTo: PRIMARY_APPLICABLE_TO,
            }
          : {}),
      };
    });
  }

  const priceMajor = Number(form.defaultPriceMinor);
  const isValid =
    form.name.trim().length > 0 &&
    form.category.trim().length > 0 &&
    form.speciesAllowed.length > 0 &&
    !Number.isNaN(priceMajor) &&
    priceMajor >= 0 &&
    isTaxRatePctValid(form.taxRatePct);

  const priceLabel = pricingModelLabel(form.category, form.defaultPricingModel);

  return (
    <div className="space-y-4">
      {/* Name */}
      <div>
        <label
          htmlFor="svc-name"
          className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
        >
          Name
        </label>
        <input
          id="svc-name"
          type="text"
          value={form.name}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder="Bath & Blow Dry"
          maxLength={80}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
      </div>

      {/* Slug */}
      <div>
        <label
          htmlFor="svc-slug"
          className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
        >
          Slug
        </label>
        <input
          id="svc-slug"
          type="text"
          value={form.slug}
          onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
          placeholder="bath-blow-dry"
          maxLength={50}
          className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
        <p className="mt-1 text-[10px] text-muted-foreground">
          Lowercase, hyphens only. Auto-derived from name.
        </p>
      </div>

      {/* Category */}
      <div>
        <label
          htmlFor="svc-category"
          className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
        >
          Category
        </label>
        <select
          id="svc-category"
          value={
            CATEGORY_OPTIONS.some((o) => o.value === form.category)
              ? form.category
              : form.category === ''
                ? ''
                : 'other'
          }
          onChange={(e) => handleCategoryChange(e.target.value)}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
        >
          <option value="" disabled>
            Select a category…
          </option>
          {CATEGORY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {/* Description */}
      <div>
        <label
          htmlFor="svc-desc"
          className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
        >
          Description (optional)
        </label>
        <textarea
          id="svc-desc"
          value={form.description}
          onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
          rows={2}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
        />
      </div>

      {/* Pricing model + price */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label
            htmlFor="svc-pricing-model"
            className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            Pricing model
          </label>
          <select
            id="svc-pricing-model"
            value={form.defaultPricingModel}
            onChange={(e) =>
              setForm((p) => ({ ...p, defaultPricingModel: e.target.value as PricingModel }))
            }
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            {PRICING_MODELS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label
            htmlFor="svc-price"
            className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            Price ({currSym}) · {priceLabel}
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">
              {currSym}
            </span>
            <input
              id="svc-price"
              type="number"
              min={0}
              step={0.01}
              value={form.defaultPriceMinor}
              onChange={(e) => setForm((p) => ({ ...p, defaultPriceMinor: e.target.value }))}
              style={{ paddingLeft: currSym.length > 1 ? '2.5rem' : '1.75rem' }}
              className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
        </div>
      </div>

      {/* Duration */}
      <div>
        <label
          htmlFor="svc-duration"
          className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
        >
          Default duration (minutes, optional)
        </label>
        <input
          id="svc-duration"
          type="number"
          min={0}
          step={1}
          value={form.defaultDurationMin}
          onChange={(e) => setForm((p) => ({ ...p, defaultDurationMin: e.target.value }))}
          placeholder="—"
          className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
      </div>

      {/* Tax rate override — shown only when the account tax engine is on */}
      {taxConfig !== null && (
        <div>
          <label
            htmlFor="svc-tax-rate"
            className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            {taxConfig.label} rate override (%)
          </label>
          <input
            id="svc-tax-rate"
            type="number"
            min={0}
            max={100}
            step={0.01}
            value={form.taxRatePct}
            onChange={(e) => setForm((p) => ({ ...p, taxRatePct: e.target.value }))}
            placeholder={`Account default (${taxConfig.accountRatePct}%)`}
            className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
          <p className="mt-1 text-[10px] text-muted-foreground">
            Leave blank to use the account rate. Enter 0 for zero-rated / exempt services.
          </p>
        </div>
      )}

      {/* Applicable to */}
      <div>
        <p className="mb-1.5 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          Applicable to
        </p>
        <div className="flex flex-wrap gap-3">
          {(
            [
              ['boarding_addon', 'Boarding add-on'],
              ['daycare_addon', 'Daycare add-on'],
              ['grooming_addon', 'Grooming add-on'],
              ['standalone', 'Standalone'],
            ] as [keyof ApplicableTo, string][]
          ).map(([key, label]) => (
            <label key={key} className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.applicableTo[key]}
                onChange={() => toggleApplicable(key)}
                className="rounded border-border"
              />
              {label}
            </label>
          ))}
        </div>
      </div>

      {/* Species */}
      <div>
        <p className="mb-1.5 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          Species
        </p>
        <div className="flex gap-3">
          {(['dog', 'cat'] as const).map((s) => (
            <label key={s} className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.speciesAllowed.includes(s)}
                onChange={() => toggleSpecies(s)}
                className="rounded border-border"
              />
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </label>
          ))}
        </div>
      </div>

      {/* Requires appointment */}
      <label className="flex cursor-pointer items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.requiresAppointment}
          onChange={(e) => setForm((p) => ({ ...p, requiresAppointment: e.target.checked }))}
          className="rounded border-border"
        />
        Requires appointment
      </label>

      {/* Migration 0095 — online booking confirmation mode */}
      <div>
        <label
          htmlFor="svc-booking-confirmation"
          className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
        >
          Online booking confirmation
        </label>
        <select
          id="svc-booking-confirmation"
          value={form.bookingConfirmation}
          onChange={(e) =>
            setForm((p) => ({
              ...p,
              bookingConfirmation: e.target.value === 'request' ? 'request' : 'instant',
            }))
          }
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
        >
          <option value="instant">Confirm instantly</option>
          <option value="request">Request — staff approve or decline</option>
        </select>
        <p className="mt-1 text-xs text-muted-foreground">
          {form.bookingConfirmation === 'request'
            ? 'Customer bookings wait in your Requests queue; capacity is held and payment is taken after approval.'
            : 'Customer bookings confirm immediately and go straight to payment.'}
        </p>
      </div>

      {/* Deposit configuration */}
      <div className="rounded-lg border border-border bg-secondary/30 p-4 space-y-3">
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            checked={form.paymentTiming === 'deposit'}
            onChange={(e) =>
              setForm((p) => ({ ...p, paymentTiming: e.target.checked ? 'deposit' : 'full' }))
            }
            className="rounded border-border"
          />
          Require deposit at booking
        </label>

        {form.paymentTiming === 'deposit' && (
          <div className="space-y-3 pt-1">
            {/* Deposit type */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor="svc-deposit-kind"
                  className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  Deposit type
                </label>
                <select
                  id="svc-deposit-kind"
                  value={form.depositKind}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, depositKind: e.target.value as 'percent' | 'fixed' }))
                  }
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  <option value="percent">Percentage</option>
                  <option value="fixed">Fixed amount</option>
                </select>
              </div>
              <div>
                {form.depositKind === 'percent' ? (
                  <>
                    <label
                      htmlFor="svc-deposit-pct"
                      className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                    >
                      Percentage (%)
                    </label>
                    <input
                      id="svc-deposit-pct"
                      type="number"
                      min={1}
                      max={100}
                      step={1}
                      value={form.depositPct}
                      onChange={(e) => setForm((p) => ({ ...p, depositPct: e.target.value }))}
                      placeholder="e.g. 25"
                      className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </>
                ) : (
                  <>
                    <label
                      htmlFor="svc-deposit-amount"
                      className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                    >
                      Fixed amount ({currSym})
                    </label>
                    <div className="relative">
                      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">
                        {currSym}
                      </span>
                      <input
                        id="svc-deposit-amount"
                        type="number"
                        min={0}
                        step={0.01}
                        value={form.depositAmountMinor}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, depositAmountMinor: e.target.value }))
                        }
                        style={{ paddingLeft: currSym.length > 1 ? '2.5rem' : '1.75rem' }}
                        className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Balance due at */}
            <div>
              <label
                htmlFor="svc-balance-due-at"
                className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
              >
                Balance due at
              </label>
              <select
                id="svc-balance-due-at"
                value={form.balanceDueAt}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    balanceDueAt: e.target.value as 'checkin' | 'checkout' | '',
                  }))
                }
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                <option value="">Not specified</option>
                <option value="checkin">Check-in</option>
                <option value="checkout">Check-out</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {error !== null && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!isValid || busy}
          onClick={() => onSubmit(form)}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
        >
          {busy ? 'Saving…' : submitLabel}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add modal
// ---------------------------------------------------------------------------

interface AddModalProps {
  initialState?: ServiceFormState | undefined;
  onClose: () => void;
  onCreated: () => void;
}

function AddModal({ initialState, onClose, onCreated }: AddModalProps) {
  const { state } = useAuthContext();
  const account = state.me?.activeAccount;
  const currSym = currencySymbol(account?.currency ?? 'GBP');
  const taxConfig =
    account?.taxEnabled === 1
      ? {
          label: account.taxLabel?.trim() || 'Tax',
          accountRatePct: String((account.taxRateBps ?? 0) / 100),
        }
      : null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(form: ServiceFormState) {
    setBusy(true);
    setError(null);
    const priceMinor = Math.round(Number.parseFloat(form.defaultPriceMinor) * 100);
    const durationMin =
      form.defaultDurationMin.trim() !== '' ? Number(form.defaultDurationMin) : null;

    const slugTrimmed = form.slug.trim();
    const body: CreateServiceInput = {
      name: form.name.trim(),
      ...(slugTrimmed ? { slug: slugTrimmed } : {}),
      description: form.description.trim() || null,
      category: form.category.trim(),
      iconSlug: form.iconSlug.trim() || null,
      applicableTo: form.applicableTo,
      requiresAppointment: form.requiresAppointment,
      defaultDurationMin: durationMin,
      defaultPricingModel: form.defaultPricingModel,
      defaultPriceMinor: priceMinor,
      speciesAllowed: form.speciesAllowed,
      paymentTiming: form.paymentTiming,
      depositKind: form.depositKind,
      depositPct:
        form.paymentTiming === 'deposit' && form.depositKind === 'percent'
          ? Number.parseInt(form.depositPct, 10) || null
          : null,
      depositAmountMinor:
        form.paymentTiming === 'deposit' && form.depositKind === 'fixed'
          ? Math.round(Number.parseFloat(form.depositAmountMinor) * 100) || null
          : null,
      balanceDueAt: form.balanceDueAt || null,
      taxRateBps: taxRateBpsFromPct(form.taxRatePct),
      bookingConfirmation: form.bookingConfirmation,
    };
    try {
      await createService(body);
      onCreated();
    } catch (err: unknown) {
      const msg = err instanceof ApiError ? err.code : 'Something went wrong';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} maxWidth="max-w-lg" aria-label="Add service">
      <div className="p-6 space-y-4">
        <h2 className="font-heading text-lg font-medium">Add service</h2>
        <ServiceForm
          initial={initialState ?? defaultFormState()}
          busy={busy}
          error={error}
          submitLabel="Add service"
          currSym={currSym}
          taxConfig={taxConfig}
          onSubmit={(form) => void handleSubmit(form)}
          onCancel={onClose}
        />
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Edit modal
// ---------------------------------------------------------------------------

interface EditModalProps {
  service: ServiceCatalogueItem;
  onClose: () => void;
  onUpdated: () => void;
}

function EditModal({ service, onClose, onUpdated }: EditModalProps) {
  const { state } = useAuthContext();
  const account = state.me?.activeAccount;
  const currSym = currencySymbol(account?.currency ?? 'GBP');
  const taxConfig =
    account?.taxEnabled === 1
      ? {
          label: account.taxLabel?.trim() || 'Tax',
          accountRatePct: String((account.taxRateBps ?? 0) / 100),
        }
      : null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(form: ServiceFormState) {
    setBusy(true);
    setError(null);
    const priceMinor = Math.round(Number.parseFloat(form.defaultPriceMinor) * 100);
    const durationMin =
      form.defaultDurationMin.trim() !== '' ? Number(form.defaultDurationMin) : null;

    const slugTrimmed = form.slug.trim();
    const patch: UpdateServiceInput = {
      name: form.name.trim(),
      ...(slugTrimmed ? { slug: slugTrimmed } : {}),
      description: form.description.trim() || null,
      category: form.category.trim(),
      iconSlug: form.iconSlug.trim() || null,
      applicableTo: form.applicableTo,
      requiresAppointment: form.requiresAppointment,
      defaultDurationMin: durationMin,
      defaultPricingModel: form.defaultPricingModel,
      defaultPriceMinor: priceMinor,
      speciesAllowed: form.speciesAllowed,
      paymentTiming: form.paymentTiming,
      depositKind: form.depositKind,
      depositPct:
        form.paymentTiming === 'deposit' && form.depositKind === 'percent'
          ? Number.parseInt(form.depositPct, 10) || null
          : null,
      depositAmountMinor:
        form.paymentTiming === 'deposit' && form.depositKind === 'fixed'
          ? Math.round(Number.parseFloat(form.depositAmountMinor) * 100) || null
          : null,
      balanceDueAt: form.balanceDueAt || null,
      taxRateBps: taxRateBpsFromPct(form.taxRatePct),
      bookingConfirmation: form.bookingConfirmation,
    };
    try {
      await updateService(service.id, patch);
      onUpdated();
    } catch (err: unknown) {
      const msg = err instanceof ApiError ? err.code : 'Something went wrong';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} maxWidth="max-w-lg" aria-label="Edit service">
      <div className="p-6 space-y-4">
        <h2 className="font-heading text-lg font-medium">Edit service</h2>
        <ServiceForm
          initial={formFromService(service)}
          busy={busy}
          error={error}
          submitLabel="Save changes"
          currSym={currSym}
          taxConfig={taxConfig}
          onSubmit={(form) => void handleSubmit(form)}
          onCancel={onClose}
        />
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Disable confirmation modal
// ---------------------------------------------------------------------------

interface DisableModalProps {
  service: ServiceCatalogueItem;
  onCancel: () => void;
  onConfirmed: () => void;
}

function DisableModal({ service, onCancel, onConfirmed }: DisableModalProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    setBusy(true);
    setError(null);
    try {
      await deleteService(service.id);
      onConfirmed();
    } catch (err: unknown) {
      const msg = err instanceof ApiError ? err.code : 'Something went wrong';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onCancel} maxWidth="max-w-md">
      <div className="p-6 space-y-4">
        <h2 className="font-heading text-lg font-medium">Disable "{service.name}"?</h2>
        <p className="text-sm text-muted-foreground">
          This service will be marked inactive and hidden from new bookings. Existing bookings that
          reference this service are preserved.
        </p>
        {error !== null && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleConfirm()}
            className="rounded-md bg-destructive px-3 py-1.5 text-sm font-medium text-destructive-foreground press disabled:opacity-50"
          >
            {busy ? 'Disabling…' : 'Disable service'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Modal state union
// ---------------------------------------------------------------------------

type ModalState =
  | { kind: 'none' }
  | { kind: 'add'; initialState?: ServiceFormState }
  | { kind: 'edit'; service: ServiceCatalogueItem }
  | { kind: 'disable'; service: ServiceCatalogueItem };

// ---------------------------------------------------------------------------
// Status pill
// ---------------------------------------------------------------------------

function StatusPill({ active }: { active: boolean }) {
  if (active) {
    return (
      <span className="inline-flex items-center rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-medium leading-none text-success ring-hairline">
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium leading-none text-muted-foreground ring-hairline">
      Inactive
    </span>
  );
}

// ---------------------------------------------------------------------------
// Core service setup cards
// ---------------------------------------------------------------------------

interface CoreServiceCardProps {
  category: CoreCategory;
  service: ServiceCatalogueItem | undefined;
  onSetUp: (category: CoreCategory) => void;
  onEdit: (service: ServiceCatalogueItem) => void;
  currency?: string;
}

function CoreServiceCard({
  category,
  service,
  onSetUp,
  onEdit,
  currency = 'GBP',
}: CoreServiceCardProps) {
  const meta: Record<
    CoreCategory,
    {
      label: string;
      rateLabel: string;
      Icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
    }
  > = {
    boarding: { label: 'Boarding', rateLabel: 'Rate per night', Icon: Moon },
    daycare: { label: 'Daycare', rateLabel: 'Rate per visit', Icon: Sun },
    grooming: { label: 'Grooming', rateLabel: 'Rate per appointment', Icon: Scissors },
  };

  const { label, rateLabel, Icon } = meta[category];
  const isConfigured = service !== undefined;

  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-background px-5 py-4">
      <div className="flex items-center gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-secondary">
          <Icon className="size-4 text-muted-foreground" strokeWidth={1.75} />
        </div>
        <div>
          <p className="text-sm font-medium">{label}</p>
          {isConfigured ? (
            <p className="text-[11px] text-muted-foreground">
              {rateLabel}:{' '}
              <span className="num font-medium text-foreground">
                {formatPriceMinor(service.defaultPriceMinor, currency)}
              </span>
              {service.isActive !== 1 && (
                <span className="ml-2 text-[10px] text-amber-600">(inactive)</span>
              )}
            </p>
          ) : (
            <p className="text-[11px] text-muted-foreground">Not configured</p>
          )}
        </div>
      </div>
      {isConfigured ? (
        <button
          type="button"
          onClick={() => onEdit(service)}
          className="flex shrink-0 items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-secondary press"
        >
          <Edit2 className="size-3" strokeWidth={1.75} />
          Edit
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onSetUp(category)}
          className="shrink-0 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground press"
        >
          Set up
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ServicesSettings() {
  const { state: authState } = useAuthContext();
  const currency = authState.me?.activeAccount?.currency ?? 'GBP';
  const [services, setServices] = useState<ServiceCatalogueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [modal, setModal] = useState<ModalState>({ kind: 'none' });

  const refresh = useCallback(async () => {
    setLoading(true);
    setPermissionDenied(false);
    try {
      const rows = await listServices();
      setServices(rows);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 403) {
        setPermissionDenied(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const displayed = showInactive ? services : services.filter((s) => s.isActive === 1);

  // Find the first active service per core category (inactive ones are also considered
  // for the setup card — show whatever exists even if inactive).
  const allActive = services.filter((s) => s.isActive === 1);
  const boardingService =
    allActive.find((s) => s.category === 'boarding') ??
    services.find((s) => s.category === 'boarding');
  const daycareService =
    allActive.find((s) => s.category === 'daycare') ??
    services.find((s) => s.category === 'daycare');
  const groomingService =
    allActive.find((s) => s.category === 'grooming') ??
    services.find((s) => s.category === 'grooming');

  const allCoreMissing = !boardingService && !daycareService && !groomingService;

  if (permissionDenied) {
    return (
      <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
        <p className="text-sm font-medium text-foreground">
          You don't have permission to manage services
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Ask an owner or manager to grant access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-medium tracking-tight">Services</h1>
        </div>
        <button
          type="button"
          onClick={() => setModal({ kind: 'add' })}
          className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press"
        >
          <Plus className="size-4" strokeWidth={1.75} />
          Add service
        </button>
      </header>

      {/* Warning banner — all core categories unconfigured */}
      {!loading && allCoreMissing && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" strokeWidth={1.75} />
          <p>
            No core services configured yet. Set up boarding, daycare, or grooming rates above
            before creating bookings.
          </p>
        </div>
      )}

      {/* Core services section */}
      <section>
        <h2 className="mb-3 font-heading text-[13px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          Core services
        </h2>
        {loading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-[62px] animate-pulse rounded-xl bg-secondary"
                style={{ opacity: 1 - i * 0.2 }}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            <CoreServiceCard
              category="boarding"
              service={boardingService}
              currency={currency}
              onSetUp={(cat) => setModal({ kind: 'add', initialState: coreFormState(cat) })}
              onEdit={(svc) => setModal({ kind: 'edit', service: svc })}
            />
            <CoreServiceCard
              category="daycare"
              service={daycareService}
              currency={currency}
              onSetUp={(cat) => setModal({ kind: 'add', initialState: coreFormState(cat) })}
              onEdit={(svc) => setModal({ kind: 'edit', service: svc })}
            />
            <CoreServiceCard
              category="grooming"
              service={groomingService}
              currency={currency}
              onSetUp={(cat) => setModal({ kind: 'add', initialState: coreFormState(cat) })}
              onEdit={(svc) => setModal({ kind: 'edit', service: svc })}
            />
          </div>
        )}
      </section>

      {/* Add-ons & additional services section */}
      <section>
        <div className="mb-3 flex items-center justify-between gap-4">
          <h2 className="font-heading text-[13px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            Add-ons &amp; additional services
          </h2>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-border"
            />
            Show inactive
          </label>
        </div>

        {loading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-14 animate-pulse rounded-lg bg-secondary"
                style={{ opacity: 1 - i * 0.2 }}
              />
            ))}
          </div>
        ) : displayed.length === 0 ? (
          <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
            <Sparkles className="mx-auto mb-4 size-10 text-muted-foreground" strokeWidth={1.75} />
            <p className="text-sm font-medium text-foreground">No services configured yet</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Add a service to start offering baths, nail trims, and more.
            </p>
            <button
              type="button"
              onClick={() => setModal({ kind: 'add' })}
              className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press"
            >
              <Plus className="size-4" strokeWidth={1.75} />
              Add service
            </button>
          </div>
        ) : (
          <section className="overflow-hidden rounded-2xl bg-surface ring-1 ring-border elevate">
            <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_auto] gap-4 border-b border-border px-6 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              <span>Name</span>
              <span>Category</span>
              <span>Price</span>
              <span>Applicable to</span>
              <span>Status</span>
              <span />
            </div>
            <div className="divide-y divide-border">
              {displayed.map((svc) => {
                const applicableTo = parseApplicableTo(svc.applicableTo);
                return (
                  <div
                    key={svc.id}
                    className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_auto] items-center gap-4 px-6 py-3.5 text-sm"
                  >
                    <span className="font-medium">{svc.name}</span>
                    <span className="text-muted-foreground">{svc.category}</span>
                    <span className="num text-muted-foreground">
                      {formatPriceMinor(svc.defaultPriceMinor, currency)}
                    </span>
                    <span className="text-muted-foreground">
                      {formatApplicableTo(applicableTo)}
                    </span>
                    <StatusPill active={svc.isActive === 1} />
                    <div className="flex items-center gap-2">
                      <a
                        href={`/settings/pricing-rules?service=${svc.id}`}
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary press"
                        title="View pricing rules for this service"
                      >
                        <ExternalLink className="size-3.5" strokeWidth={1.75} />
                        Pricing
                      </a>
                      <button
                        type="button"
                        onClick={() => setModal({ kind: 'edit', service: svc })}
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground press"
                        title="Edit service"
                      >
                        <Edit2 className="size-3.5" strokeWidth={1.75} />
                        Edit
                      </button>
                      {svc.isActive === 1 && (
                        <button
                          type="button"
                          onClick={() => setModal({ kind: 'disable', service: svc })}
                          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive press"
                          title="Disable service"
                        >
                          <ToggleLeft className="size-3.5" strokeWidth={1.75} />
                          Disable
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </section>

      {/* Modals */}
      {modal.kind === 'add' && (
        <AddModal
          initialState={modal.initialState}
          onClose={() => setModal({ kind: 'none' })}
          onCreated={() => {
            setModal({ kind: 'none' });
            void refresh();
          }}
        />
      )}

      {modal.kind === 'edit' && (
        <EditModal
          service={modal.service}
          onClose={() => setModal({ kind: 'none' })}
          onUpdated={() => {
            setModal({ kind: 'none' });
            void refresh();
          }}
        />
      )}

      {modal.kind === 'disable' && (
        <DisableModal
          service={modal.service}
          onCancel={() => setModal({ kind: 'none' })}
          onConfirmed={() => {
            setModal({ kind: 'none' });
            void refresh();
          }}
        />
      )}
    </div>
  );
}
